import React from "react";

import styled from "styled-components";

import Spacer, { Axis } from "~/components/Spacer";
import { VaspDueDiligenceReviewStatus } from "~/templates/companies/types";

const RadioButonCard = styled.div<{ isSelected: boolean }>`
  width: 400px;
  height: 115px;
  background: white;

  display: flex;

  padding: 0px 28px;
  border-radius: 4px;
  transition: 0.2s ease-out;

  ${(params) => params.isSelected && `box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);`}
  border: ${(props) => (props.isSelected ? "1.5px solid #1f1f1f" : "1px solid #e9e9e9")};
  background: ${(props) => (props.isSelected ? "white" : "#f2f2f2")};

  &:hover {
    cursor: pointer;
    box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
  }
`;

const ContentContainer = styled.div.attrs({ type: "radiogroup" })`
  display: flex;
  align-items: center;
`;

const RadioButtonInput = styled.input.attrs({ type: "radio" })`
  accent-color: black;
  margin-top: 4px;
  align-self: flex-start;
`;

const RadioButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatusTitle = styled.div`
  font-style: normal;
  font-weight: 800;
  font-size: 16px;
  line-height: 18px;

  text-transform: uppercase;
  color: #000000;
`;

const StatusDescriptionSummary = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 17px;
  color: #000000;
`;

const StatusDescriptionContext = styled(StatusDescriptionSummary)`
  font-weight: 400;
`;

const CenterVertically = styled.div`
  display: flex;
  align-items: center;
`;

const TitleIconContainer = styled.div`
  display: flex;
  align-items: center;
`;

type VaspDueDiligencePanelProps = {
  vaspReviewStatus?: VaspDueDiligenceReviewStatus | null;
  onValueSelected: (vaspReviewStatus: VaspDueDiligenceReviewStatus) => void;
};

export default function VaspReviewRadioButtons({
  vaspReviewStatus = null,
  onValueSelected,
}: VaspDueDiligencePanelProps) {
  const handleRadioButtonClicked = (value: VaspDueDiligenceReviewStatus) => {
    onValueSelected(value);
  };

  return (
    <RadioButtonsContainer>
      <RadioButonCard
        isSelected={vaspReviewStatus === VaspDueDiligenceReviewStatus.MANUAL}
        onClick={() => handleRadioButtonClicked(VaspDueDiligenceReviewStatus.MANUAL)}
      >
        <ContentContainer>
          <CenterVertically>
            <div>
              <RadioButtonInput
                aria-labelledby="label1"
                checked={vaspReviewStatus === VaspDueDiligenceReviewStatus.MANUAL}
                onChange={(data) => handleRadioButtonClicked(data.target.value as VaspDueDiligenceReviewStatus)}
                value={VaspDueDiligenceReviewStatus.MANUAL}
              />
            </div>
          </CenterVertically>
          <Spacer axis={Axis.HORIZONTAL} size={16} />
          <div>
            <TitleIconContainer>
              <img src={"/assets/monitored-vasp.svg"} />
              <Spacer axis={Axis.HORIZONTAL} size={8} />
              <StatusTitle id="label1">Add to your list of monitored VASPs</StatusTitle>
            </TitleIconContainer>
            <Spacer axis={Axis.VERTICAL} size={8} />
            <StatusDescriptionSummary>
              For VASPs you want to continuously keep an eye on and review transfers on a case-by-case basis
            </StatusDescriptionSummary>
          </div>
        </ContentContainer>
      </RadioButonCard>

      <Spacer axis={Axis.VERTICAL} size={12} />
      <RadioButonCard
        isSelected={vaspReviewStatus === VaspDueDiligenceReviewStatus.TRUSTED}
        onClick={() => handleRadioButtonClicked(VaspDueDiligenceReviewStatus.TRUSTED)}
      >
        <ContentContainer>
          <CenterVertically>
            <div>
              <RadioButtonInput
                aria-labelledby="label2"
                checked={vaspReviewStatus === VaspDueDiligenceReviewStatus.TRUSTED}
                onChange={(data) => handleRadioButtonClicked(data.target.value as VaspDueDiligenceReviewStatus)}
                value={VaspDueDiligenceReviewStatus.TRUSTED}
              />
            </div>
          </CenterVertically>
          <Spacer axis={Axis.HORIZONTAL} size={16} />
          <div>
            <TitleIconContainer>
              <img src={"/assets/trusted-vasp.svg"} />
              <Spacer axis={Axis.HORIZONTAL} size={8} />
              <StatusTitle id="label2">Add to your list of trusted vasps</StatusTitle>
            </TitleIconContainer>
            <Spacer axis={Axis.VERTICAL} size={8} />
            <StatusDescriptionSummary>
              For VASPs you decide are low risk and trust to send/receive transfers
            </StatusDescriptionSummary>
          </div>
        </ContentContainer>
      </RadioButonCard>

      <Spacer axis={Axis.VERTICAL} size={12} />
      <RadioButonCard
        isSelected={vaspReviewStatus === VaspDueDiligenceReviewStatus.BLOCKED}
        onClick={() => handleRadioButtonClicked(VaspDueDiligenceReviewStatus.BLOCKED)}
      >
        <ContentContainer>
          <CenterVertically>
            <div>
              <RadioButtonInput
                aria-labelledby="label3"
                checked={vaspReviewStatus === VaspDueDiligenceReviewStatus.BLOCKED}
                onChange={(data) => handleRadioButtonClicked(data.target.value as VaspDueDiligenceReviewStatus)}
                value={VaspDueDiligenceReviewStatus.BLOCKED}
              />
            </div>
          </CenterVertically>
          <Spacer axis={Axis.HORIZONTAL} size={16} />
          <div>
            <TitleIconContainer>
              <img src={"/assets/blocked-vasp.svg"} />
              <Spacer axis={Axis.HORIZONTAL} size={8} />
              <StatusTitle id="label3">Add to your list of blocked VASPs</StatusTitle>
            </TitleIconContainer>
            <Spacer axis={Axis.VERTICAL} size={8} />
            <StatusDescriptionSummary>
              For VASPs you decide are high risk and do not trust to send/receive transfers
            </StatusDescriptionSummary>
          </div>
        </ContentContainer>
      </RadioButonCard>
    </RadioButtonsContainer>
  );
}
